import React, { useState, useMemo, useEffect, useCallback } from "react";
import AdminHeader from "../Header/AdminHeader";
import AdminFooter from "../Footer/AdminFooter";
import AdminLeftMenu from "../Navbar/AdminLeftMenu";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSitemap, faBuilding, faUsers, faUserTie, faChevronDown, faChevronRight,
  faSearch, faLayerGroup, faIdBadge, faClock, faSpinner, faUserCircle, faCircle,
} from "@fortawesome/free-solid-svg-icons";
import { userloginDetails } from "../Utils/AdminAuth";
import Axios from "../../axios/Axios";
import { HOST_URL } from "../Dashboard/api/Api";
import styles from "./ResellerHierarchy.module.css";

// Node shape (from /admin/reseller_tree):
// { id, username, user_type: "reseller" | "emp", reseller, parent, created,
//   reseller_count, client_count, children: [ ...same shape ] }
//
// Each node renders as a card with two columns: "Resellers" (its own
// node.children, each recursively its own card with the same two columns)
// and "Client Accounts" (that SAME node's own direct clients only — fetched
// once from admin_user_list.php and filtered client-side by
// `parent === node.id`). A reseller's clients therefore always show under
// that reseller's own card, never bubbled up into its parent's column.

const TYPE_BADGE = {
  reseller: { label: "Reseller", color: "#4338ca", bg: "#eef2ff", border: "#e0e7ff", icon: faBuilding },
  emp: { label: "Employee", color: "#15803d", bg: "#f0fdf4", border: "#dcfce7", icon: faUserCircle },
};

const STAT_COLORS = {
  resellers: { color: "#4f46e5", bg: "#eef2ff" },
  employees: { color: "#16a34a", bg: "#f0fdf4" },
  roots: { color: "#2563eb", bg: "#eff6ff" },
  clients: { color: "#ea580c", bg: "#fff7ed" },
};

const CLIENTS_PER_PAGE = 10;

function dateFmt(str) {
  if (!str) return "—";
  const d = new Date(String(str).replace(" ", "T"));
  if (isNaN(d.getTime())) return str;
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

// Recursively count nodes of a given user_type across the whole tree.
function countByType(nodes, type) {
  return (nodes || []).reduce((sum, n) => {
    let c = n.user_type === type ? 1 : 0;
    if (n.children && n.children.length) c += countByType(n.children, type);
    return sum + c;
  }, 0);
}

// Prune the tree down to branches that match the query, keeping a matched
// node's own subtree intact but trimming subtrees that matched only via a
// descendant.
function filterTree(nodes, query) {
  if (!query) return nodes;
  const q = query.toLowerCase();
  const result = [];
  (nodes || []).forEach(n => {
    const selfMatch = n.username?.toLowerCase().includes(q) || String(n.id).includes(q);
    const filteredChildren = filterTree(n.children, query);
    if (selfMatch) {
      result.push(n);
    } else if (filteredChildren.length) {
      result.push({ ...n, children: filteredChildren });
    }
  });
  return result;
}

const StatusDot = ({ active }) => (
  <FontAwesomeIcon icon={faCircle} style={{ fontSize: "7px", color: active ? "#16a34a" : "#cbd5e1", marginRight: "5px" }} />
);

const SectionLabel = ({ icon, text }) => (
  <div style={{ display: "flex", alignItems: "center", gap: "7px", marginBottom: "10px" }}>
    <FontAwesomeIcon icon={icon} style={{ color: "#94a3b8", fontSize: "11px" }} />
    <span style={{ fontSize: "11px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.6px" }}>{text}</span>
  </div>
);

const MiniBadge = ({ icon, label, color, bg }) => (
  <span style={{
    display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "11px", fontWeight: "700",
    color, background: bg, padding: "5px 10px", borderRadius: "100px", whiteSpace: "nowrap",
  }}>
    <FontAwesomeIcon icon={icon} style={{ fontSize: "10px" }} />{label}
  </span>
);

const EmptyNote = ({ text }) => (
  <span style={{ fontSize: "12px", color: "#94a3b8" }}>{text}</span>
);

const EMPTY_AGENT_SUMMARY = {
  total_agents: 0,
  active_agents: 0,
  inactive_agents: 0,
  client_agents: { total_agents: 0, active_agents: 0, inactive_agents: 0 },
  reseller_agents: { total_agents: 0, active_agents: 0, inactive_agents: 0 },
};

const AGENT_STAT_COLORS = {
  total: { color: "#4f46e5", bg: "#eef2ff" },
  active: { color: "#16a34a", bg: "#f0fdf4" },
  inactive: { color: "#dc2626", bg: "#fef2f2" },
};

const AgentStat = ({ label, value, color, bg }) => (
  <div style={{
    flex: "1 1 80px", minWidth: "80px", background: bg, borderRadius: "10px",
    padding: "9px 10px", textAlign: "center",
  }}>
    <div style={{ fontSize: "17px", fontWeight: "800", color, lineHeight: 1 }}>{value ?? 0}</div>
    <div style={{ fontSize: "10px", fontWeight: "700", color: "#64748b", marginTop: "4px", textTransform: "uppercase", letterSpacing: "0.3px" }}>
      {label}
    </div>
  </div>
);

const AgentGroup = ({ title, stats }) => (
  <div style={{ flex: "1 1 220px", minWidth: "220px", background: "#fff", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "12px" }}>
    <div style={{ fontSize: "11px", fontWeight: "700", color: "#475569", marginBottom: "9px", textTransform: "uppercase", letterSpacing: "0.4px" }}>
      {title}
    </div>
    <div style={{ display: "flex", gap: "8px" }}>
      <AgentStat label="Total" value={stats.total_agents} color={AGENT_STAT_COLORS.total.color} bg={AGENT_STAT_COLORS.total.bg} />
      <AgentStat label="Active" value={stats.active_agents} color={AGENT_STAT_COLORS.active.color} bg={AGENT_STAT_COLORS.active.bg} />
      <AgentStat label="Inactive" value={stats.inactive_agents} color={AGENT_STAT_COLORS.inactive.color} bg={AGENT_STAT_COLORS.inactive.bg} />
    </div>
  </div>
);

// Agent summary panel — fetches /api/agent_management (reseller_agent_summary)
// for this exact node's own id, every time this node's accordion is opened.
const AgentSummaryPanel = ({ node, depth, clientsCtx }) => {
  const { userId, userToken } = clientsCtx;
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchAgentSummary = async () => {
      setLoading(true);
      setError(null);
      try {
        const payload = {
          user_id: userId,
          token: userToken,
          method: "reseller_agent_summary",
          reseller_id: node.id,
        };
        const { data } = await Axios.post(`https://napi.authkey.io/api/agent_management`, payload);
        if (cancelled) return;
        setSummary(data.success && data.data ? data.data : EMPTY_AGENT_SUMMARY);
      } catch (err) {
        if (cancelled) return;
        setError(err?.response?.data?.message || err.message || "Network error.");
        setSummary(EMPTY_AGENT_SUMMARY);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchAgentSummary();
    return () => { cancelled = true; };
  }, [node.id, userId, userToken]);

  const s = summary || EMPTY_AGENT_SUMMARY;

  return (
    <div style={{
      borderTop: "1px solid #f1f5f9", background: "#f8fafc",
      padding: depth === 0 ? "16px 20px" : "12px 16px",
    }}>
      <SectionLabel icon={faUserTie} text="Agent Summary" />
      {loading ? (
        <div style={{ display: "flex", alignItems: "center", gap: "8px", color: "#64748b", fontSize: "12px" }}>
          <FontAwesomeIcon icon={faSpinner} spin /> Loading agent summary…
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          {error && (
            <span style={{ fontSize: "12px", color: "#b91c1c" }}>Could not load agent summary: {error}. Showing 0.</span>
          )}
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            <AgentStat label="Total Agents" value={s.total_agents} color={AGENT_STAT_COLORS.total.color} bg={AGENT_STAT_COLORS.total.bg} />
            <AgentStat label="Active Agents" value={s.active_agents} color={AGENT_STAT_COLORS.active.color} bg={AGENT_STAT_COLORS.active.bg} />
            <AgentStat label="Inactive Agents" value={s.inactive_agents} color={AGENT_STAT_COLORS.inactive.color} bg={AGENT_STAT_COLORS.inactive.bg} />
          </div>
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            <AgentGroup title="Reseller Agents" stats={s.reseller_agents || EMPTY_AGENT_SUMMARY.reseller_agents} />
            <AgentGroup title="Client Agents" stats={s.client_agents || EMPTY_AGENT_SUMMARY.client_agents} />
          </div>
        </div>
      )}
    </div>
  );
};

const ClientRow = ({ client }) => (
  <div style={{
    display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap",
    background: "#eff6ff", border: "1px solid #dbeafe", borderRadius: "10px", padding: "9px 12px",
  }}>
    <div style={{
      width: "26px", height: "26px", borderRadius: "50%", flexShrink: 0,
      background: "#ffffff", border: "1px solid #bfdbfe", display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <FontAwesomeIcon icon={faUserTie} style={{ color: "#2563eb", fontSize: "11px" }} />
    </div>
    <div style={{ flex: 1, minWidth: "140px" }}>
      <div
        title={client.email || client.username}
        style={{
          fontSize: "12px", fontWeight: "600", color: "#334155",
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}
      >
        {client.email || client.username}
      </div>
      <div style={{ fontSize: "11px", color: "#94a3b8", display: "flex", alignItems: "center", marginTop: "1px" }}>
        <StatusDot active={client.is_active === 1 || client.is_active === "1"} />
        ID {client.id}
        <span style={{ margin: "0 6px", color: "#e2e8f0" }}>|</span>
        Joined {dateFmt(client.created)}
      </div>
    </div>
    {client.plan_name && (
      <span style={{
        fontSize: "10px", fontWeight: "700", padding: "3px 9px", borderRadius: "100px",
        color: "#a16207", background: "#fefce8", textTransform: "uppercase", letterSpacing: "0.3px",
      }}>
        {client.plan_name}
      </span>
    )}
  </div>
);

// ── One reseller/employee card: header + two columns (its Resellers, its
// own Client Accounts). Recursive — every sub-reseller is the same card. ──
const NodeCard = ({ node, depth, clientsCtx }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [clientPage, setClientPage] = useState(1);

  const open = isOpen;
  const hasSubNodes = node.children && node.children.length > 0;
  const canExpand = hasSubNodes || (node.client_count || 0) > 0;
  const type = TYPE_BADGE[node.user_type] || { label: node.user_type || "User", color: "#475569", bg: "#f1f5f9", border: "#e2e8f0", icon: faUserCircle };

  useEffect(() => {
    if (open && (node.client_count || 0) > 0) clientsCtx.requestClients();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const myClients = useMemo(
    () => (clientsCtx.allUsers || []).filter(u => Number(u.parent) === Number(node.id)),
    [clientsCtx.allUsers, node.id]
  );
  const totalClientPages = Math.max(1, Math.ceil(myClients.length / CLIENTS_PER_PAGE));
  const clientPageItems = myClients.slice((clientPage - 1) * CLIENTS_PER_PAGE, clientPage * CLIENTS_PER_PAGE);

  return (
    <div style={{
      background: depth === 0 ? "#fff" : type.bg,
      border: `1px solid ${depth === 0 ? "#e2e8f0" : type.border}`, borderRadius: depth === 0 ? "16px" : "12px",
      boxShadow: depth === 0 ? "0 1px 4px rgba(0,0,0,0.05)" : "none",
      overflow: "hidden",
    }}>
      {/* Header row */}
      <div
        onClick={canExpand ? () => {
          // A text-selection drag ending inside this row still fires a
          // native click — don't let that toggle the accordion.
          const sel = window.getSelection();
          if (sel && sel.toString().length > 0) return;
          setIsOpen(v => !v);
        } : undefined}
        className={styles.nodeHeader}
        style={{
          display: "flex", alignItems: "center", gap: depth === 0 ? "14px" : "10px",
          padding: depth === 0 ? "16px 20px" : "10px 14px",
          cursor: canExpand ? "pointer" : "default", userSelect: "none",
        }}
      >
        <span style={{ width: "12px", display: "flex", justifyContent: "center", color: "#94a3b8", fontSize: "12px", flexShrink: 0 }}>
          {canExpand ? <FontAwesomeIcon icon={open ? faChevronDown : faChevronRight} /> : null}
        </span>

        <div style={{
          width: depth === 0 ? "42px" : "30px", height: depth === 0 ? "42px" : "30px",
          borderRadius: depth === 0 ? "12px" : "50%",
          background: depth === 0 ? type.bg : "#ffffff",
          border: depth === 0 ? "none" : `1px solid ${type.border}`,
          flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <FontAwesomeIcon icon={type.icon} style={{ color: type.color, fontSize: depth === 0 ? "16px" : "13px" }} />
        </div>

        <div style={{ flex: "1 1 150px", minWidth: "150px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span
              title={node.username}
              style={{
                flex: "1 1 auto", minWidth: 0, display: "block",
                fontSize: depth === 0 ? "15px" : "13px", fontWeight: "700", color: "#0f172a",
                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                userSelect: "text", cursor: "text",
              }}
            >
              {node.username}
            </span>
            <span style={{
              flexShrink: 0, whiteSpace: "nowrap",
              fontSize: "10px", fontWeight: "700", padding: "2px 9px", borderRadius: "100px",
              background: type.bg, color: type.color, border: `1px solid ${type.border}`,
              textTransform: "uppercase", letterSpacing: "0.4px",
            }}>
              {type.label}
            </span>
          </div>
          <div style={{ fontSize: "12px", color: "#64748b", marginTop: "3px", userSelect: "text", cursor: "text" }}>
            <FontAwesomeIcon icon={faIdBadge} style={{ marginRight: "5px", fontSize: "10px" }} />ID {node.id}
            <span style={{ margin: "0 8px", color: "#e2e8f0" }}>|</span>
            <FontAwesomeIcon icon={faClock} style={{ marginRight: "5px", fontSize: "10px" }} />Since {dateFmt(node.created)}
          </div>
        </div>

        <div className={styles.nodeBadges} style={{ display: "flex", gap: "8px", flexShrink: 0 }}>
          <MiniBadge icon={faLayerGroup} label={`${node.reseller_count || 0} Resellers`} color="#ea580c" bg="#fff7ed" />
          <MiniBadge icon={faUserTie} label={`${node.client_count || 0} Clients`} color="#2563eb" bg="#eff6ff" />
        </div>
      </div>

      {/* Agent summary — only for reseller nodes, refetched on every open */}
      {open && node.user_type === "reseller" && (
        <AgentSummaryPanel node={node} depth={depth} clientsCtx={clientsCtx} />
      )}

      {/* Two columns: Resellers | Client Accounts */}
      {open && (
        <div className={styles.nodeColumns} style={{ borderTop: "1px solid #f1f5f9", padding: depth === 0 ? "18px 20px" : "14px 16px", display: "flex", flexWrap: "wrap" }}>

          {/* Resellers column — this node's own children, recursively */}
          <div className={styles.nodeColumn}>
            <SectionLabel icon={faLayerGroup} text="Resellers" />
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {hasSubNodes ? (
                node.children.map(child => (
                  <NodeCard key={child.id} node={child} depth={depth + 1} clientsCtx={clientsCtx} />
                ))
              ) : (
                <EmptyNote text="No sub-resellers under this account." />
              )}
            </div>
          </div>

          {/* Client Accounts column — this node's own direct clients only */}
          <div className={styles.nodeColumn}>
            <SectionLabel icon={faUserTie} text="Client Accounts" />
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {(node.client_count || 0) === 0 ? (
                <EmptyNote text="No client accounts under this user." />
              ) : clientsCtx.loading && myClients.length === 0 ? (
                <div style={{ display: "flex", alignItems: "center", gap: "8px", color: "#64748b", fontSize: "12px" }}>
                  <FontAwesomeIcon icon={faSpinner} spin /> Loading client accounts…
                </div>
              ) : clientsCtx.error ? (
                <span style={{ fontSize: "12px", color: "#b91c1c" }}>Could not load clients: {clientsCtx.error}</span>
              ) : myClients.length === 0 ? (
                <EmptyNote text={`${node.client_count} client account${node.client_count === 1 ? "" : "s"} reported, but none matched in the loaded user list.`} />
              ) : (
                <>
                  {clientPageItems.map(c => <ClientRow key={c.id} client={c} />)}
                  {totalClientPages > 1 && (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingTop: "2px" }}>
                      <span style={{ fontSize: "11px", color: "#94a3b8" }}>
                        Page {clientPage} of {totalClientPages} · {myClients.length} total
                      </span>
                      <div style={{ display: "flex", gap: "6px" }}>
                        <button
                          type="button"
                          disabled={clientPage === 1}
                          onClick={() => setClientPage(p => p - 1)}
                          style={{
                            border: "1px solid #e2e8f0", borderRadius: "6px", background: clientPage === 1 ? "#f8fafc" : "#fff",
                            color: clientPage === 1 ? "#cbd5e1" : "#2563eb", fontSize: "11px", fontWeight: "600",
                            padding: "3px 10px", cursor: clientPage === 1 ? "not-allowed" : "pointer",
                          }}
                        >
                          Prev
                        </button>
                        <button
                          type="button"
                          disabled={clientPage >= totalClientPages}
                          onClick={() => setClientPage(p => p + 1)}
                          style={{
                            border: "1px solid #e2e8f0", borderRadius: "6px", background: clientPage >= totalClientPages ? "#f8fafc" : "#fff",
                            color: clientPage >= totalClientPages ? "#cbd5e1" : "#2563eb", fontSize: "11px", fontWeight: "600",
                            padding: "3px 10px", cursor: clientPage >= totalClientPages ? "not-allowed" : "pointer",
                          }}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ResellerHierarchy = () => {
  const lStorage = userloginDetails();
  const userId = lStorage?.user?.userId;
  const userToken = lStorage?.token;
  const adminType = lStorage?.user?.isAdmin;

  const [search, setSearch] = useState("");
  const [hierarchyDataTree, setHierarchyDataTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Flat platform-wide user list, fetched once (lazily, on first node expand
  // that reports a client_count) and filtered client-side per node by
  // `parent === node.id` — there is no server-side "clients by reseller id"
  // endpoint, so this mirrors the pattern used across the rest of Admin.
  const [allUsers, setAllUsers] = useState(null);
  const [allUsersLoading, setAllUsersLoading] = useState(false);
  const [allUsersError, setAllUsersError] = useState(null);

  const hierarchyData = useCallback(async () => {
    setLoading(true);
    setError(null);
    let paramData = {
      user_id: userId,
      token: userToken,
    };
    try {
      let { data } = await Axios.post(`https://napi3.authkey.io/api2/v1/admin/reseller_tree`, paramData);
      if (data.success) {
        setHierarchyDataTree(Array.isArray(data.data) ? data.data : []);
      } else {
        setError(data.message || "Failed to load reseller hierarchy.");
      }
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Network error.");
    } finally {
      setLoading(false);
    }
  }, [userId, userToken]);

  useEffect(() => {
    hierarchyData();
  }, [hierarchyData]);

  const requestClients = useCallback(async () => {
    if (allUsers !== null || allUsersLoading) return;
    setAllUsersLoading(true);
    setAllUsersError(null);
    try {
      const paramData = {
        user_id: userId,
        method: "retrieve_all_user",
        type: adminType,
        token: userToken,
      };
      const { data } = await Axios.post(`${HOST_URL}/admin_user_list.php`, paramData);
      if (data.success) {
        setAllUsers(Array.isArray(data.data) ? data.data : []);
      } else {
        setAllUsersError(data.message || "Failed to load client accounts.");
      }
    } catch (err) {
      setAllUsersError(err?.response?.data?.message || err.message || "Network error.");
    } finally {
      setAllUsersLoading(false);
    }
  }, [userId, userToken, adminType, allUsers, allUsersLoading]);

  const clientsCtx = useMemo(() => ({
    allUsers, loading: allUsersLoading, error: allUsersError, requestClients, userId, userToken,
  }), [allUsers, allUsersLoading, allUsersError, requestClients, userId, userToken]);

  const filteredTree = useMemo(() => filterTree(hierarchyDataTree, search.trim()), [hierarchyDataTree, search]);

  const totals = useMemo(() => ({
    roots: hierarchyDataTree.length,
    resellers: countByType(hierarchyDataTree, "reseller"),
    employees: countByType(hierarchyDataTree, "emp"),
    clients: hierarchyDataTree.reduce((sum, n) => sum + (n.client_count || 0), 0),
  }), [hierarchyDataTree]);

  const STAT_TILES = [
    { key: "resellers", label: "Total Resellers", value: totals.resellers, icon: faBuilding },
    { key: "employees", label: "Total Employees", value: totals.employees, icon: faUsers },
    { key: "roots", label: "Root Accounts", value: totals.roots, icon: faSitemap },
    { key: "clients", label: "Total Client Accounts", value: totals.clients, icon: faUserTie },
  ];

  return (
    <div className="wrapper">
      <AdminLeftMenu />
      <div className="main">
        <AdminHeader />
        <main className="content">
          <div className="container-fluid p-0" style={{ fontFamily: "Inter, system-ui, -apple-system, sans-serif" }}>

            {/* ── Page header ── */}
            <div style={{ marginBottom: "20px" }}>
              <span style={{ fontSize: "11px", textTransform: "uppercase", letterSpacing: "1.5px", color: "#6366f1", fontWeight: "700" }}>
                Partner Network
              </span>
              <h3 className={styles.pageTitle} style={{ margin: "4px 0 0 0", fontWeight: "800", color: "#0f172a" }}>
                <FontAwesomeIcon icon={faSitemap} style={{ marginRight: "10px", color: "#4f46e5" }} />
                Reseller Hierarchy
              </h3>
              <p className={styles.pageSubtitle} style={{ margin: "4px 0 0 0", color: "#64748b" }}>
                Every reseller/employee's own sub-resellers and its own client accounts, nested exactly where they belong.
              </p>
            </div>

            {/* ── Error banner ── */}
            {error && (
              <div style={{
                display: "flex", alignItems: "flex-start", gap: "12px",
                background: "#fef2f2", border: "1px solid #fecaca",
                borderRadius: "12px", padding: "14px 18px", marginBottom: "16px",
              }}>
                <span style={{ fontSize: "18px", lineHeight: 1 }}>&#9888;</span>
                <div>
                  <div style={{ fontSize: "14px", fontWeight: "700", color: "#dc2626", marginBottom: "2px" }}>Something went wrong</div>
                  <div style={{ fontSize: "13px", color: "#b91c1c" }}>{error}</div>
                </div>
              </div>
            )}

            {/* ── Stat tiles ── */}
            <div className={styles.statGrid} style={{ display: "grid", marginBottom: "24px" }}>
              {STAT_TILES.map(tile => {
                const c = STAT_COLORS[tile.key];
                return (
                  <div key={tile.key} style={{
                    background: "#fff", border: "1px solid #e2e8f0", borderRadius: "14px",
                    padding: "16px 18px", boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
                    display: "flex", flexDirection: "column", gap: "10px",
                  }}>
                    <div style={{
                      width: "34px", height: "34px", borderRadius: "10px", background: c.bg,
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      <FontAwesomeIcon icon={tile.icon} style={{ color: c.color, fontSize: "14px" }} />
                    </div>
                    <div>
                      <div style={{ fontSize: "24px", fontWeight: "800", color: "#0f172a", lineHeight: 1 }}>{tile.value}</div>
                      <div style={{ fontSize: "12px", fontWeight: "600", color: "#64748b", marginTop: "4px" }}>{tile.label}</div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* ── Search ── */}
            <div className={styles.searchWrap} style={{ position: "relative", marginBottom: "18px" }}>
              <FontAwesomeIcon icon={faSearch} style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8", fontSize: "12px" }} />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by email or user ID…"
                style={{
                  width: "100%", height: "38px", paddingLeft: "34px", paddingRight: "12px",
                  border: "1px solid #e2e8f0", borderRadius: "10px", fontSize: "13px",
                  color: "#334155", outline: "none", background: "#fff", boxSizing: "border-box",
                }}
              />
            </div>

            {/* ── Hierarchy ── */}
            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              {loading ? (
                <div style={{
                  background: "#fff", border: "1px solid #e2e8f0", borderRadius: "14px",
                  padding: "48px 0", display: "flex", flexDirection: "column", alignItems: "center", gap: "12px",
                }}>
                  <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: "26px", color: "#4f46e5" }} />
                  <span style={{ color: "#64748b", fontSize: "13px" }}>Loading reseller hierarchy…</span>
                </div>
              ) : filteredTree.length === 0 ? (
                <div style={{
                  background: "#fff", border: "1px solid #e2e8f0", borderRadius: "14px",
                  padding: "48px 0", textAlign: "center", color: "#94a3b8", fontSize: "13px",
                }}>
                  {search ? <>No resellers, employees or clients match “{search}”.</> : "No reseller hierarchy data available."}
                </div>
              ) : (
                filteredTree.map(node => (
                  <NodeCard key={node.id} node={node} depth={0} clientsCtx={clientsCtx} />
                ))
              )}
            </div>
          </div>
        </main>
        <AdminFooter />
      </div>
    </div>
  );
};

export default ResellerHierarchy;
